import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentDiscussionIcon} from '@primer/octicons-react'
import {Button, Tooltip} from '@primer/react'
import {useRef} from 'react'
import {clsx} from 'clsx'

import {CounterSkeleton} from './CounterSkeleton'

import {CommentsSidePanel} from './CommentsSidePanel'
import type {PageLimits} from '../../page-data/payloads/files'
import type {AliveChannels} from '../../page-data/payloads/header'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {
  usePullRequestBasePath,
  useUnresolvedThreadCount,
  useLayoutAuthorLogin,
  useUnresolvedConversationCount,
} from '../../stores/layout-selectors'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface OpenCommentsSidePanelButtonProps {
  pageLimits?: PageLimits
  aliveChannels: AliveChannels
  tabSize?: number
  className?: string
}

/**
 * Renders a button that opens the comments panel
 */
export function OpenCommentsPanelButton({
  pageLimits,
  aliveChannels,
  tabSize,
  className,
}: OpenCommentsSidePanelButtonProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Comments)

  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()
  const isOpen = isDockedPanelOpen
  const buttonAriaLabel = isDockedPanelOpen ? 'Close comments panel' : 'Open comments panel'
  const basePath = usePullRequestBasePath()
  const authorLogin = useLayoutAuthorLogin()

  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const unresolvedConversationCount = useUnresolvedConversationCount()
  const legacyDiffCommentCount = useUnresolvedThreadCount()

  const isLoading = showUpdatedHeader && unresolvedConversationCount === null
  const diffCommentCount = showUpdatedHeader ? (unresolvedConversationCount ?? 0) : legacyDiffCommentCount

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  return (
    <div className={clsx('d-flex flex-items-center', className)}>
      <ErrorBoundary
        boundaryName="OpenCommentsPanelButton"
        fallback={
          <Button
            aria-label="The comments panel cannot currently be opened."
            leadingVisual={CommentDiscussionIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Tooltip ref={togglePanelRef} text={buttonAriaLabel} direction="sw" type="label">
          <Button
            aria-expanded={isOpen}
            count={isLoading ? undefined : diffCommentCount}
            leadingVisual={CommentDiscussionIcon}
            trailingVisual={isLoading ? CounterSkeleton : undefined}
            size="small"
            variant="invisible"
            onClick={() => {
              sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
              actions.togglePanel('comments', {returnFocusTo: togglePanelRef.current})
            }}
          />
        </Tooltip>
        <CommentsSidePanel
          isOpen={isDockedPanelOpen}
          pageLimits={pageLimits}
          pathName={basePath}
          pullRequestAuthorLogin={authorLogin}
          aliveChannels={aliveChannels}
          tabSize={tabSize}
          toggleSidesheetRef={togglePanelRef}
          onClose={actions.closePanel}
        />
      </ErrorBoundary>
    </div>
  )
}
